import { useState, useMemo, useReducer } from "react"
import PropTypes from "prop-types"
import { omit, toUpper, find, get, size } from "lodash"
import moment from "moment"
import { TextField, FormControl, FormControlLabel, DialogContent, DialogActions, Checkbox, Button } from "@mui/material"
import DatePicker from "components/date-form-picker"
import CurrencySelector from "components/currency-selector"
import { validateSwapTicker } from "helpers/swaps"
import { SWAP } from "./shapes"
import classNames from "classnames"
import "./style.scss"
import Loading from "components/loading"
import Error from "components/error"
import { useDefaultCurrencyId } from "resources/account"
import { useBaskets } from "resources/baskets"
import { useSupportedCurrencies } from "resources/currencies"
import { combineStatuses, LOADING, ERROR } from "helpers/data-statuses"

function validateTickerNotUsed(ticker, baskets) {
  if (baskets) {
    const foundBasket = find(baskets, elem => toUpper(elem.ticker) === toUpper(ticker))
    if (foundBasket) {
      return false
    }
  }
  return true
}

function formReducer(state, update) {
  return {
    ...state,
    ...update,
  }
}

function EditSwap({
  selectedSwap,
  saveSwap,
  applyAction,
  close,
}) {
  const [baskets, basketsStatus] = useBaskets()
  const [supportedCurrencies, currenciesStatus] = useSupportedCurrencies()
  const status = useMemo(() => combineStatuses([basketsStatus, currenciesStatus]), [basketsStatus, currenciesStatus])
  const defaultCurrencyId = useDefaultCurrencyId()

  const [formData, setFormData] = useReducer(formReducer, {
    description: get(selectedSwap, "description", ""),
    ticker: get(selectedSwap, "ticker", ""),
    existingBasket: !!get(selectedSwap, "ticker"),
    terminationDate: moment(get(selectedSwap, "terminationDate")).isValid() ? get(selectedSwap, "terminationDate") : "",
    currencyOverride: get(selectedSwap, "currencyOverride"),
  })

  const [errors, setErrors] = useState({})
  const [currencyOverrideChecked, setCurrencyOverrideChecked] = useState(!!get(selectedSwap, "currencyOverride"))

  const submitDisabled = useMemo(() => {
    const { ticker, description, currencyOverride } = formData
    return !ticker || !description || (currencyOverrideChecked && !currencyOverride) || size(errors) > 0
  }, [formData, currencyOverrideChecked, errors])

  const onTickerChange = event => {
    const { value } = event.target
    let validationErrors = { ...errors }

    if (!validateSwapTicker(value)) {
      validationErrors.ticker = true
    } else {
      validationErrors = omit(validationErrors, "ticker")
    }

    if (!validateTickerNotUsed(value, baskets)) {
      validationErrors.tickerExists = true
    } else {
      validationErrors = omit(validationErrors, "tickerExists")
    }

    setFormData({ ticker: value })
    setErrors(validationErrors)
  }

  if (status === LOADING) {
    return <Loading />
  }

  if (status === ERROR) {
    return <Error />
  }

  const onDescriptionChange = event => {
    const { value } = event.target

    if (!value) {
      setErrors({ ...errors, description: true })
    } else if (value) {
      setErrors(omit(errors, "description"))
    }

    setFormData({ description: value })
  }

  const onTerminationDateChange = date => {
    setErrors(omit(errors, "terminationDate"))
    setFormData({ terminationDate: date })
  }

  const onTerminationDateError = () => {
    setErrors({ ...errors, terminationDate: true })
  }

  const validateSwap = () => {
    const { ticker, description, existingBasket } = formData
    const errors = {}

    if (!validateSwapTicker(ticker)) {
      errors.ticker = true
    }

    if (!existingBasket && !validateTickerNotUsed(ticker, baskets)) {
      errors.tickerExists = true
    }

    if (!description) {
      errors.description = true
    }

    if (currencyOverrideChecked && !formData.currencyOverride) {
      errors.currencyOverride = true
    }

    return errors
  }

  const submitSwap = () => {
    const { ticker, description, terminationDate, currencyOverride } = formData
    saveSwap(ticker, { description, terminationDate, currencyOverride })
  }

  const onSaveClicked = () => {
    const validationErrors = validateSwap()
    if (size(validationErrors)) {
      setErrors({ ...errors, ...validationErrors })
    } else if (applyAction) {
      applyAction(submitSwap, null, null, true)()
    } else {
      submitSwap()
    }
  }

  const onKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      const validationErrors = validateSwap()
      setErrors(validationErrors)
      if (!size(validationErrors)) {
        submitSwap()
      }
    }
  }

  const onCurrencyOverrideChecked = (event) => {
    setCurrencyOverrideChecked(event.target.checked)
    setFormData({ currencyOverride: null })
    setErrors(omit(errors, "currencyOverride"))
  }

  const onCurrencyOverrideChanged = (_, currencyId) => {
    if (currencyId) {
      setErrors(omit(errors, "currencyOverride"))
    } else {
      setErrors({ ...errors, currencyOverride: true })
    }
    setFormData({ currencyOverride: currencyId })
  }

  const getTickerHelperText = () => {
    if (!formData.existingBasket) {
      if (errors.tickerExists) {
        return "Ticker already exists in your account."
      }

      return errors.ticker ? "Invalid ticker. Please follow naming guidelines." : "3-20 chars long. Alphanum, _, -. Spaces not allowed."
    }
  }

  const { ticker, description, existingBasket, terminationDate, currencyOverride } = formData
  return <div className="components_edit-swap">
    <DialogContent className="edit-swap-content-wrapper">
      <div className="swap-input-wrapper">
        <FormControl
          variant="standard"
          className={classNames("swap-ticker-wrapper", existingBasket ? "swap-ticker-wrapper-disabled" : "")}
        >
          <TextField
            variant="standard"
            disabled={existingBasket}
            id="swap-ticker"
            name="ticker"
            className="text-input"
            label="Basket Ticker"
            value={ticker}
            error={errors.ticker || errors.tickerExists}
            onChange={onTickerChange}
            onKeyDown={onKeyPress}
            helperText={getTickerHelperText()}
            slotProps={{
              formHelperText: { error: errors.ticker || errors.tickerExists, classes: { root: "swap-input-error" } },
            }}
          />
        </FormControl>
        <FormControl variant="standard" className="swap-description-wrapper">
          <TextField
            variant="standard"
            name="description"
            className="text-input"
            multiline
            label="Description"
            value={description}
            error={errors.description}
            onChange={onDescriptionChange}
            onKeyDown={onKeyPress}
            helperText={errors.description ? "Description is mandatory." : ""}
            slotProps={{
              formHelperText: { error: errors.description, classes: { root: "swap-input-error" } },
            }}
          />
        </FormControl>
        <FormControl variant="standard" className="swap-terminationdate-wrapper">
          <DatePicker
            selectedDate={terminationDate}
            onChange={onTerminationDateChange}
            onError={onTerminationDateError}
            clearable
            label="Termination Date"
            helperText="This optional field can be used to wind down all positions in this basket, i.e. close out all positions to 0, on the date provided"
            slotProps={{
              openPickerButton: {
                "data-test-class": "CalendarIcon",
              },
            }}
          />
        </FormControl>
        <FormControl variant="standard">
          <FormControlLabel
            id="currency-override-setting"
            className="optional-setting"
            control={
              <Checkbox
                checked={currencyOverrideChecked}
                onChange={onCurrencyOverrideChecked}
              />
            }
            label={`Override default account currency (${defaultCurrencyId})`}
          />
        </FormControl>
        {currencyOverrideChecked && <FormControl variant="standard">
          <CurrencySelector
            supportedCurrencies={supportedCurrencies}
            value={currencyOverride}
            onChange={onCurrencyOverrideChanged}
            onKeyPress={onKeyPress}
            error={errors.currencyOverride}
          />
        </FormControl>}
      </div>
    </DialogContent>
    <DialogActions>
      <Button color="secondary" onClick={close}>Cancel</Button>
      <Button color="primary" disabled={submitDisabled} onClick={onSaveClicked}>
        {existingBasket ? "Update basket" : "Create basket"}
      </Button>
    </DialogActions>
  </div>
}

EditSwap.propTypes = {
  selectedSwap: SWAP,
  saveSwap: PropTypes.func.isRequired,
  applyAction: PropTypes.func,
  close: PropTypes.func,
}

export default EditSwap
